import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";

const LINKS = [
  { to: "/about", label: "About" },
  { to: "/capabilities", label: "Capabilities" },
  { to: "/explorer", label: "Market Explorer" },
  { to: "/trust", label: "Trust" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export const HeaderNav = ({ onRequestInventory }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="header-nav"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-[#0E0E10]/80 border-b border-white/10 shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" data-testid="nav-logo" aria-label="Sunrays home">
          <Logo size={40} textClass="text-3xl lg:text-4xl" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  isActive ? "text-[#C9A24B]" : "text-[#B8BCC2] hover:text-[#C9A24B]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-request-inventory-btn"
            onClick={onRequestInventory}
            className="gold-btn hidden !px-6 !py-2.5 lg:inline-flex"
          >
            Request Inventory
          </button>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="glass-btn !rounded-full !p-2.5 lg:hidden"
            aria-label="Menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3 }}
            className="glass-panel mx-4 mb-4 rounded-2xl p-4 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            {LINKS.map((l) => (
              <button
                key={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => {
                  setOpen(false);
                  navigate(l.to);
                }}
                className="block w-full rounded-lg px-4 py-3 text-left text-sm text-[#B8BCC2] transition-colors hover:bg-white/5 hover:text-[#C9A24B]"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-request-inventory-btn"
              onClick={() => {
                setOpen(false);
                onRequestInventory();
              }}
              className="gold-btn mt-2 w-full"
            >
              Request Inventory
            </button>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
